import React from 'react';
import user_icon from '../../assets/icons/user.svg';
import { useTranslation } from 'react-i18next';

interface Record {
  id: number;
  name: string;
  phone: string;
  email: string;
  alcoholtests: {
    id: number;
    data: string;
  }[];
  profile_picture?: string;
}

interface SubmissionTableProps {
  records: Record[];
}

const SubmissionTable: React.FC<SubmissionTableProps> = ({ records }) => {
  const isRowEmpty = (record: Record): boolean =>
    record.alcoholtests.every((test) => !test.data);
  const { t } = useTranslation('dashboard');

  const getBackgroundColor = (data: string): string => {
    const value = parseFloat(data);
    if (value === 0.0) return 'bg-gray-100 py-1 px-2 rounded-md';
    if (value > 0.0 && value < 0.15)
      return 'bg-yellow-200 py-1 px-2 rounded-md';
    if (value >= 0.15) return 'bg-red-200 py-1 px-2 rounded-md';
    return '';
  };

  const getTextColor = (data: string): string => {
    const value = parseFloat(data);
    if (value === 0.0) return 'text-green-600';
    if (value > 0.0 && value < 0.15) return 'text-yellow-600';
    if (value >= 0.15) return 'text-red-600';
    return 'text-caption';
  };

  const getOrdinalSuffix = (index: number) => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const relevantIndex = index + 1; // Index starts from 0, so add 1 for display
    const mod100 = relevantIndex % 100;

    if (mod100 >= 11 && mod100 <= 13) {
      return 'th';
    }

    const mod10 = relevantIndex % 10;
    return suffixes[mod10] || suffixes[0];
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-full rounded-md my-4">
        {/* Table Header */}
        <div className="bg-primary/50 rounded-md text-caption">
          <div className="flex p-2 h-8 items-center">
            <p className="w-9/12">{t('table.driver-name')}</p>
            {Array.from({ length: 8 }, (_, index) => (
              <p key={index} className="w-2/5 text-center">
                {t(`table.${index + 1}${getOrdinalSuffix(index)}-record`)}
              </p>
            ))}
          </div>
        </div>
        {/* Table Body */}
        <div>
          {records.map((record) => (
            <div key={record.id} className="flex h-16 border-b items-center">
              <div className="flex items-center py-1 px-2 w-9/12 space-x-2 truncate">
                <img
                  src={record.profile_picture || user_icon}
                  alt="User Icon"
                  className="w-10 h-10 object-cover rounded-full"
                />
                <div>
                  <p className="text-body-2">{record.name}</p>
                  <p className="text-caption text-neutral/75">
                    {record.phone || record.email}
                  </p>
                </div>
              </div>
              {Array.from({ length: 8 }, (_, index) => (
                <div
                  key={index}
                  className="flex w-2/5 justify-center align-center overflow-hidden text-caption"
                >
                  <span
                    className={`inline-block max-w-full truncate ${getBackgroundColor(
                      index < record.alcoholtests.length
                        ? record.alcoholtests[index].data
                        : ''
                    )} ${getTextColor(
                      index < record.alcoholtests.length
                        ? record.alcoholtests[index].data
                        : ''
                    )}`}
                  >
                    {index === 0 && isRowEmpty(record)
                      ? `${t('table.no-data')}`
                      : index < record.alcoholtests.length
                      ? `${record.alcoholtests[index].data} mg/L`
                      : ''}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubmissionTable;
